import React from 'react';

import cn from 'classnames';
import Lottie from 'lottie-react';
import { useMediaQuery } from '@mui/material';

import { AvatarDisplay, VideoDisplay, VoiceIndicator } from '@src/components/VideoBox/partials';

import { isValidVideoStream } from '@src/helpers';

import { useEmojiAnimations } from '@src/hooks';
import useVideoBox from '@src/components/VideoBox/useVideoBox';

import HandRaise from '@src/assets/animations/HandRaise.json';

import { IVideoStream, TVideoObjectFit } from '@src/types';

import styles from './pipAvatar.module.scss';

interface IProps extends IVideoStream {
  style?: React.CSSProperties;
  showName?: boolean;
  displaySmallAvatar?: boolean;
  onVideoClick?: () => void;
  isWbActive?: boolean;
  objectFit?: TVideoObjectFit;
}

const PipAvatar = ({
  style,
  onVideoClick,
  displaySmallAvatar,
  isWbActive,
  showName = true,
  objectFit,
  ...videoStream
}: IProps) => {
  const {
    videoRef,
    wavyScale,
    peerEmoji,
    isMicMuted,
    audioVolumeTmp,
    isPeerTalking,
    handRaiseOrder,
    selectedVideoObjectFit,
    isMirrorActive,
    handleMouseEnter,
    handleMouseLeave,
    stream: activeStream,
  } = useVideoBox(videoStream);

  const { animations } = useEmojiAnimations();

  const isMobile = useMediaQuery('(max-width:768px)');

  const { id, muted, poster, peerId, peerName, isCurrentUser } = videoStream;

  const isHandRaised = handRaiseOrder !== null;

  return (
    <div
      className={cn(styles.videoScreenContainer, {
        [styles.videoScreenContainerMobile]: isMobile,
        [styles.boxBorder]: isPeerTalking,
      })}
      style={{
        ...(style && { ...style }),
      }}
      role="button"
      tabIndex={0}
      onMouseEnter={() => handleMouseEnter()}
      onMouseLeave={() => handleMouseLeave()}
      onClick={() => {
        onVideoClick?.();
      }}
    >
      {activeStream && isValidVideoStream(activeStream) ? (
        <VideoDisplay
          videoRef={videoRef}
          isMirrorActive={isMirrorActive}
          muted={muted}
          poster={poster}
          objectFit={objectFit ?? selectedVideoObjectFit}
          id={id}
        />
      ) : (
        <AvatarDisplay peerId={peerId} peerName={peerName} audioVolumeTmp={audioVolumeTmp} />
      )}
      <div className="absolute left-2 bottom-1 flex items-center gap-2" title={peerName}>
        {isCurrentUser && <VoiceIndicator isMicMuted={isMicMuted} wavyScale={wavyScale} />}
        <span className={cn(styles.pipLabel, 'text-xs max-w-[58px] truncate overflow-hidden whitespace-nowrap')}>
          {isCurrentUser ? 'You' : peerName}
        </span>
      </div>
      {/* Emoji animation at the container level, not in the footer */}
      <div className={styles.videoEmojiIcon}>
        {!!peerEmoji && animations[peerEmoji] && (
          <div className={cn(styles.pulseIcon)}>
            <Lottie
              id={`video-box-emoji_${peerEmoji}`}
              animationData={animations[peerEmoji]}
              style={{ width: 41, height: 54 }}
            />
          </div>
        )}
        {isHandRaised && (
          <div className={cn(styles.pulseIcon)}>
            <Lottie
              animationData={HandRaise}
              loop={false}
              style={{ width: 41, height: 54 }}
              rendererSettings={{ className: 'lottie-white' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PipAvatar;
